import { JSX, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import {
    AppShell,
    AppShellStylesNames,
    Box,
    Button,
    Container,
    createStyles,
    CSSObject,
    Group,
    Header,
    MantineSize,
    MediaQuery,
    Navbar,
    Space,
    Title,
} from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import { Menu, MenuItem } from '../Menu/Menu.tsx';
import { CssObjectsVarios } from '../Styles/Varios.tsx';
import { BurgerToggleMenu } from './BurgerToggleMenu.tsx';

/**
 * Estos son los estilos de AppShell, que si usa nombres predefinidos
 */
const useAppShellStyles = createStyles<AppShellStylesNames>(
    (theme): Record<AppShellStylesNames, CSSObject> => {
        return {
            /**
             * AppShell main element, contains content
             */
            main: {
                [theme.fn.smallerThan('sm')]: {
                    //En mobile saco el margen, para ganar espacio
                    padding: 0,
                },
            },
            /**
             * 	Root element, wraps Navbar, Header and content
             */
            root: {
                backgroundImage: 'url(/fondo.svg)',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                backgroundColor: '#e9f4fb',
            },
            /**
             * AppShell body, contains Navbar and main element
             */
            body: {},
        };
    }
);

/**
 * Estos son los estilos generales, no usan nombres predefinidos por mantine
 */
const useStyles = createStyles((theme) => {
    return {
        header: {
            // width: '1180',
            backgroundColor: theme.colors.celesteLuker[1],
            border: 'none',
            display: 'flex',
            padding: '18px 28px',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            '& h1': {
                display: 'flex',
                height: '33px',
                padding: '20px 0px',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0,
            },
        },
        container: {
            [theme.fn.smallerThan('sm')]: {
                //esto es lo que hace que los cuadros vayan hasta el borde de la pantalla
                // en mobile
                padding: 0,
            },
        },
        navBar: {
            border: 'none',
            padding: '0 3px',
            height: '100vh',
            backgroundColor: theme.colors.negroLuker[8],
            // backgroundColor: 'red',},
        },
        logoLukerBox: {
            //18 para arriba y abajo esta bien
            //3 es para que tenga el mismo espacio a la izquierda que los items del menu
            padding: '18px 3px',
            '& img': {
                /**
                 * 40 es para que no sea demasiado grande
                 */
                height: '40px',
            },
        },
    };
});

export interface MainLayoutProps {
    titulo: string;
    /**
     * @deprecated
     */
    menu?: JSX.Element;
    menuItems: MenuItem[];
    tiendaNombre?: string;
    containerSize?: MantineSize;
    fluid?: boolean;
}

function Logo({ className }: { className: string }) {
    return (
        <MediaQuery
            smallerThan="sm"
            styles={CssObjectsVarios.displayNone}
        >
            <Box className={className}>
                <img
                    alt={'Luker'}
                    src="/luker_celeste.svg"
                />
            </Box>
        </MediaQuery>
    );
}

export function MainLayout(props: PropsWithChildren<MainLayoutProps>) {
    const { classes } = useStyles();
    const { classes: appShellClasses } = useAppShellStyles();

    /**
     * Esto controla el menu colapsado, solo en pantalla grande
     */
    const [collapsed, setCollapsed] = useLocalStorage({
        key: 'MainLayout.collapseMenu',
        defaultValue: false,
    });
    const collapseMenu = useCallback(() => setCollapsed(true), [setCollapsed]);
    const extendMenu = useCallback(() => setCollapsed(false), [setCollapsed]);

    /**
     * Esto controla si se muestra o no el menu, solo en pantalla chica
     */
    const [opened, setOpened] = useState(false);
    const toggleOpened = useCallback(() => setOpened((o) => !o), [setOpened]);

    /**
     * El ancho de la navBar si o si tiene que ser un objeto con keys...
     * así que no podemos zafar del useMemo
     */
    const navBarWidth = useMemo(
        () =>
            collapsed
                ? { base: 70 }
                : /**
                   * Esto creo que significa que solo a partir de lg, su ancho es 200, antes nada
                   */
                  { lg: 220 },
        [collapsed]
    );
    const navbar = (
        <Navbar
            hiddenBreakpoint="sm"
            hidden={!opened}
            className={classes.navBar}
            c={'celesteLuker.1'}
            width={navBarWidth}
        >
            <BurgerToggleMenu
                opened={opened}
                toggleOpened={toggleOpened}
            />
            {collapsed ? null : <Logo className={classes.logoLukerBox} />}
            <Menu
                menuItems={props.menuItems}
                onCollapse={collapseMenu}
                onExtend={extendMenu}
                collapsed={collapsed}
            />
        </Navbar>
    );

    const header = (
        <Header
            height={74}
            className={classes.header}
        >
            <BurgerToggleMenu
                opened={opened}
                toggleOpened={toggleOpened}
            />

            {process.env.NODE_ENV !== 'test' &&
            !window.location.href.includes('.luker.io') ? (
                <Group>
                    <Title fz={'1.25rem'}>{props.titulo}</Title>
                    <Button
                        variant={'gradient'}
                        component={'a'}
                        href={window.location.href.replace(
                            '.dluker.io',
                            '.luker.io'
                        )}
                    >
                        Ver en PROD
                    </Button>
                </Group>
            ) : (
                <Title fz={'1.25rem'}>{props.titulo}</Title>
            )}

            {/*TODO: reemplazar esto de tiendaNombre con el selector de tienda */}
            <Title fz={'1.25rem'}>{props.tiendaNombre}</Title>
        </Header>
    );

    return (
        <AppShell
            layout="alt"
            padding="md"
            navbarOffsetBreakpoint="sm"
            asideOffsetBreakpoint="sm"
            navbar={navbar}
            header={header}
            classNames={appShellClasses}
        >
            <Container
                className={classes.container}
                size={props.containerSize}
                fluid={props.fluid}
            >
                <Space h="md" />
                {props.children}
            </Container>
        </AppShell>
    );
}
